import { useState } from 'react';
import { Operatory } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/operatory.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  Button,
  Tray,
  ModalControlModalProps,
  Chip,
  useAlert,
  useForm,
  useFormField,
  useDebouncedValue,
} from '@frontend/design-system';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';
import { AvailabilityContent } from './availability-content';
import { RecurrenceType } from './types';

type AvailabilityTrayProps = {
  modalProps: ModalControlModalProps;
  operatory: Operatory;
  locationId: string;
};

export const AvailabilityTray = ({ modalProps, operatory, locationId }: AvailabilityTrayProps) => {
  const { t } = useTranslation('schedule-settings');
  const { isMultiLocation, getLocationName } = useSchedulingLocationInfo();
  const alert = useAlert();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: providersData } = SchedulerV3.Helpers.useGetProvidersV3Data({
    selectedLocationId: locationId,
  });

  const appointmentTypeSearch = useFormField({ type: 'text' });
  const debouncedValue = useDebouncedValue(appointmentTypeSearch.value, 500);

  const { data: appointmentTypesData } = SchedulerV3.Helpers.useGetAppointmentTypesV3Data({
    selectedLocationId: locationId,
    selectedLocationIds: [locationId],
    debouncedSearchValue: debouncedValue.length >= 2 ? debouncedValue : undefined,
  });

  const { getFieldProps, formProps } = useForm({
    fields: {
      recurrence: {
        type: 'dropdown',
        required: true,
        value: RecurrenceType.WEEKLY,
      },
      interval: {
        type: 'number',
        max: 31,
        min: 1,
        required: true,
      },
      timeUnit: {
        type: 'dropdown',
        required: true,
      },
      daysOfWeek: {
        type: 'multiselect',
        required: true,
        minRequired: 1,
      },
      startDate: {
        type: 'date',
        required: true,
      },
      startTime: {
        type: 'time',
        required: true,
      },
      endTime: {
        type: 'time',
        required: true,
      },
      providers: {
        type: 'multiselect',
        required: true,
        minRequired: 1,
      },
      appointmentTypes: {
        type: 'checklist',
        required: true,
        minRequired: 1,
      },
      selfBookingEnabled: {
        type: 'switch',
        value: false,
      },
    },
    onSubmit: async (formValues) => {
      console.log('Submitting availability with values:', formValues);
      try {
        setIsSubmitting(true);
        alert.success(t('Availability saved successfully'));
        // modalProps.onClose();
      } catch (error) {
        console.error('Failed to save availability:', error);
        alert.error(t('Failed to save availability'));
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const sortedProviderOptions = (providersData?.providers || []).sort((a, b) =>
    (a.displayName || a.firstName + ' ' + a.lastName || '').localeCompare(
      b.displayName || b.firstName + ' ' + b.lastName || ''
    )
  );

  const sortedAppointmentTypeOptions = (appointmentTypesData?.appointmentTypes || []).sort((a, b) =>
    (a.displayName || a.externalName || '').localeCompare(b.displayName || b.externalName || '')
  );

  const handleCancel = () => {
    modalProps.onClose();
  };

  return (
    <Tray {...modalProps} width='large' disableCloseOnEscape>
      <Tray.Header Buttons={<Button size='large' variant='secondary' iconName='x' onClick={modalProps.onClose} />}>
        {t('Add Availability for {{operatoryName}}', {
          operatoryName: operatory.displayName || operatory.externalName,
        })}
      </Tray.Header>
      <Tray.Body>
        {isMultiLocation && (
          <>
            <Chip.Location css={{ maxWidth: 250 }}>{getLocationName(locationId)}</Chip.Location>
            <hr css={{ margin: `${theme.spacing(2)} 0`, border: `1px solid ${theme.colors.neutral20}` }} />
          </>
        )}
        <AvailabilityContent
          formProps={formProps}
          getFieldProps={getFieldProps}
          providerOptions={sortedProviderOptions}
          appointmentTypeOptions={sortedAppointmentTypeOptions}
          appointmentTypeSearch={appointmentTypeSearch}
        />
      </Tray.Body>
      <Tray.Actions
        primaryLabel={t('Save')}
        onPrimaryClick={(event) => {
          if (event) {
            formProps.onSubmit(event);
          }
        }}
        secondaryLabel={t('Cancel')}
        onSecondaryClick={handleCancel}
        disabledPrimary={isSubmitting}
      />
    </Tray>
  );
};
